import React, { useContext } from 'react';
import Lazy from '@prisjakt/ui-lazy';
import { CurrentCampaign } from '../../../types';
import PriceRow, { columnCount } from '../../../../../components/price-row';
import { getValidPriceType } from '../utils';
import { Brand, Maybe, Price, ProductMedia } from '../../../../../types';
import { SortOption } from '../../sort-options/types';
import { PriceListFilterContext } from '../../../price-list-filter-context';

const PriceListTableRow: React.FC<IPriceListTableRowProps> = props => {
  const {
    item,
    index,
    campaign,
    dispatchProductPriceClick,
    dispatchProductClickOutBounce,
    splitList,
    ...priceRowProps
  } = props;
  const { filters } = useContext(PriceListFilterContext);
  const rowKey = `${item.id}-${index}`;

  return wrapLazy(
    index,
    rowKey,
    <PriceRow
      key={rowKey}
      name={item.name}
      {...priceRowProps}
      onClickOutBounce={dispatchProductClickOutBounce}
      priceId={item.id}
      priceType={getValidPriceType(item.type)}
      price={item.price}
      deliveryOptions={item.deliveryOptions}
      store={item.store}
      storeInfo={
        item.store && item.store.providedByStore
          ? item.store.providedByStore.generalInformation
          : null
      }
      stock={item.stock}
      externalUri={item.externalUri}
      alternativePrices={item.alternativePrices}
      openInNewWindow
      brandRecommendedRetailer={Boolean(item.brandRecommendedRetailer)}
      isInternationalPrice={Boolean(
        filters.showInternational &&
          item.store &&
          item.store.market === 'secondary'
      )}
      campaign={getCampaignById(item, campaign)}
      onPriceClick={(
        featured: boolean,
        price: number | undefined,
        status: string,
        isAlternative: boolean
      ) => {
        // NOTE: Any changes done to data or structure of PRODUCT_PRICE_CLICK needs to be
        // reflected in react/middlewares/out pre click for tracking clicks before JS bundle
        dispatchProductPriceClick(
          priceRowProps.productId,
          price || item,
          featured,
          status,
          isAlternative,
          splitList
        );
      }}
    />
  );
};

interface IPriceListTableRowProps {
  item: Price;
  index: number;
  productId: number;
  productName?: string;
  media: ProductMedia & { first?: string | null };
  brand?: Maybe<Brand>;
  campaign?: CurrentCampaign | null;
  dispatchProductPriceClick: (
    id: number,
    price: Price | number,
    featured: boolean,
    status: string,
    isAlternative: boolean,
    isSplitList?: boolean
  ) => void;
  dispatchProductClickOutBounce: (id: number, priceId?: string | null) => void;
  currentSort: SortOption;
  splitList?: boolean;
}

const lazyPlaceholderStyle = { height: 57 };

const lazyPlaceholder = (
  <tr>
    <td colSpan={columnCount} style={lazyPlaceholderStyle} />
  </tr>
);

const getCampaignById = (item: Price, campaign: any) => {
  return campaign?.price &&
    campaign?.media &&
    campaign?.name &&
    item.id === campaign.price.id
    ? campaign
    : null;
};

const wrapLazy = (
  order: number,
  rowKey: string,
  component: React.ReactElement
) => {
  if (order > 10) {
    return (
      <Lazy placeholder={lazyPlaceholder} offset={200} key={rowKey}>
        {component}
      </Lazy>
    );
  }
  return component;
};

export default PriceListTableRow;
